/**
 * userActionHandlers
 *
 * Colocated plain module (NOT a hook, NOT a component) that builds the
 * side-effecting handlers for the User Actions sheet. These handlers
 * orchestrate sheets, navigation, toasts and auth state — presentation
 * concerns that must stay out of `useUserActions` (the layer rule keeps
 * `Navigator.*`, `SheetService`, `ConfirmSheet`, toasts and `dispatch(logout())`
 * out of the hook). They are extracted here purely to keep
 * `UserActionsContent.tsx` under the component-size guideline.
 *
 * Matches Flutter implementation: lib/presentation/bottom_sheet/user_actions_bottom_sheet.dart
 */

import React from 'react';
import toast from 'react-hot-toast';
import type { AppDispatch } from '@/presentation/store';
import type { ResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { l10n } from '@/presentation/locale/l10n';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserActionEntity } from '@/domain/entities/userAction/userActionEntity';
import { UserActionType } from '@/domain/enums/userAction/userActionType';
import { env } from '@/config/env';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { SearchUsersSheet } from '../../searchUsers/SearchUsersSheet';
import { ShareSocialSheet } from '../../shareSocial/ShareSocialSheet';
import { buildSocialShareProps } from '@/presentation/feature/share/buildSocialShareProps';
import { generateUserProfileShareDataUseCase } from '@/domain/usecases/share/generateUserProfileShareDataUseCase';
import { ShareType } from '@/domain/enums/shareType';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { HelpAboutSheet } from '@/presentation/components/common/sheet/helpAbout/HelpAboutSheet';
import { ReportUserSheet } from '../../reportUser/ReportUserSheet';
import { AppealUserReportSheet } from '../../appealUserReport/AppealUserReportSheet';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { Navigator } from '@/presentation/routing/Navigator';
import { logout } from '@/presentation/store/slices/auth/authSlice';
import { signOutUseCase } from '@/domain/usecases/login/signOutUseCase';

/**
 * Presentation dependencies the handlers close over. Supplied fresh on every
 * render by `UserActionsContent` so the handlers always see the latest `user`,
 * `l10n`, block state, etc.
 */
export interface UserActionHandlersDeps {
  user: UserEntity | null;
  screenKey?: string;
  responsive: ResponsiveDesign;
  l10n: l10n;
  dispatch: AppDispatch;
  saveToFavorites: () => Promise<boolean>;
  blockUser: () => Promise<boolean>;
  userIsBlocked: boolean;
}

export interface UserActionHandlers {
  /** Routes a tapped action to its side-effecting handler. */
  handleActionClick: (action: UserActionEntity) => void;
}

export const createUserActionHandlers = ({
  user,
  screenKey,
  responsive,
  l10n,
  dispatch,
  saveToFavorites,
  blockUser,
  userIsBlocked,
}: UserActionHandlersDeps): UserActionHandlers => {
  const handleShareWithFriends = () => {
    if (!user) return;

    // Create ShareInfo entity
    const shareInfo: ShareInfoEntity = {
      type: ShareType.user,
      shareLink: user.shareLink || `${env.DOMAIN_NAME}/${user.firebaseId}`,
      usersInfo: {
        userId: user.firebaseId,
        name: user.name,
        username: user.username,
      },
    };

    SearchUsersSheet.show(
      shareInfo,
      null,
      screenKey,
      responsive.searchUsersSheet.width
    );
  };

  const handleSocialShare = async () => {
    if (!user) return;

    const shareContent = await generateUserProfileShareDataUseCase.execute(user);
    const socialShareProps = buildSocialShareProps(user, shareContent);

    // Try native Web Share API first (best on mobile, supports AirDrop on Safari)
    if (navigator.share) {
      try {
        // Open a 'protective' sheet so that, when tapping outside the
        // share modal to close, it doesn't close the underlying sheet.
        SheetService.show({
          title: '',
          content: React.createElement('div'),
          container: null,
          screenKey,
          fixedHeight: '0vh',
          width: '0vw',
          closeOnClickOutside: false,
        });

        const shareData: ShareData = shareContent.file
          ? { title: shareContent.title, text: `${shareContent.text}\n\n${shareContent.shareUrl}`, files: [shareContent.file] }
          : { title: shareContent.title, text: shareContent.text, url: shareContent.shareUrl };

        await navigator.share(shareData);
        SheetService.close();
      } catch (error) {
        SheetService.close();
        const err = error instanceof Error ? error : new Error(String(error));
        if (err.message === 'Share canceled' || err.name === 'AbortError') {
          return;
        }
        appLogger.error('Error sharing user profile', 'userActionHandlers.handleSocialShare', { error: err });
        toast.error(l10n.userActions.socialShareError);
        ShareSocialSheet.show(socialShareProps, null, screenKey);
      }
    } else {
      // Browser doesn't support Web Share API - show fallback share sheet
      ShareSocialSheet.show(socialShareProps, null, screenKey);
    }
  };

  const handleSaveToFavorites = async () => {
    const success = await saveToFavorites();
    if (!success) toast.error(l10n.common.genericError);
  };

  const handleBlockUser = async () => {
    if (!user) return;

    const isBlocked = userIsBlocked;
    const title = isBlocked
      ? l10n.userActions.unblockUserTitle.replace('{firstName}', user.firstName)
      : l10n.userActions.blockUserTitle.replace('{firstName}', user.firstName);
    const confirmText = isBlocked
      ? l10n.userActions.unblockUserDescription
      : l10n.userActions.blockUserDescription;
    const okText = isBlocked ? l10n.userActions.unblockUser : l10n.userActions.blockUser;

    await ConfirmSheet.show({
      title,
      confirmText,
      okText,
      cancelText: l10n.common.cancel,
      screenKey,
      onConfirm: async () => {
        const success = await blockUser();
        if (!success) {
          appLogger.error(
            'Failed to block/unblock user',
            'userActionHandlers.handleBlockUser', { context: { willBlock: !isBlocked } },
          );
          toast.error(l10n.userActions.blockError);
        }
      },
      loadingStyle: 'button',
    });
  };

  const handleLogout = async () => {
    let signOutFailed = false;
    const response = await ConfirmSheet.show({
      title: l10n.userActions.logoutConfirmTitle,
      confirmText: l10n.userActions.logoutConfirmMessage,
      okText: l10n.userActions.logout,
      cancelText: l10n.common.cancel,
      screenKey,
      loadingStyle: 'button',
      onConfirm: async () => {
        try {
          await signOutUseCase.execute();
        } catch (error) {
          appLogger.error('Logout error', 'userActionHandlers.handleLogout', { error: error as Error });
          toast.error(l10n.common.genericError);
          signOutFailed = true;
        }
      },
    });

    if (!response || signOutFailed) return;

    // Close all sheets before flipping auth state so their
    // portal containers aren't torn down mid-route-swap.
    SheetService.closeAll();
    dispatch(logout());
  };

  const handleActionClick = (action: UserActionEntity) => {
    switch (action.type) {
      case UserActionType.SHARE:
        handleShareWithFriends();
        break;
      case UserActionType.SHARE_SOCIAL:
        handleSocialShare();
        break;
      case UserActionType.SAVE:
        handleSaveToFavorites();
        break;
      case UserActionType.BLOCK:
        handleBlockUser();
        break;
      case UserActionType.REPORT:
        if (user) ReportUserSheet.show(user, null, screenKey, responsive.reportContentSheet.fixedHeight);
        break;
      case UserActionType.APPEAL:
        AppealUserReportSheet.show(null, screenKey, responsive.appealUserReportSheet.fixedHeight);
        break;
      case UserActionType.SETTINGS:
        Navigator.navigateToSettings();
        break;
      case UserActionType.EDIT:
        Navigator.navigateToEditUserProfile();
        break;
      case UserActionType.HELP_ABOUT:
        HelpAboutSheet.show({ screenKey });
        break;
      case UserActionType.LOGOUT:
        handleLogout();
        break;
      case UserActionType.DELETE_ACCOUNT:
        Navigator.navigateToDeleteAccount();
        break;
    }
  };

  return { handleActionClick };
};
